import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'convenienceDispatch.selectedFixedRouteByDate';
const MAX_ENTRIES = 30;

type Entry = { fixedRouteId: number; updatedAt: number };
type Store = Record<string, Entry>;

function isEntry(value: unknown): value is Entry {
  return (
    !!value
    && typeof value === 'object'
    && typeof (value as { fixedRouteId?: unknown }).fixedRouteId === 'number'
    && typeof (value as { updatedAt?: unknown }).updatedAt === 'number'
  );
}

function readStore(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Store = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (isEntry(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeStore(input: Store): void {
  let store = input;
  // LRU: 上限超過時は updatedAt 降順で先頭 N 件のみ残す
  const entries = Object.entries(store);
  if (entries.length > MAX_ENTRIES) {
    entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
    store = Object.fromEntries(entries.slice(0, MAX_ENTRIES));
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota / disabled errors
  }
}

/**
 * 選択中の固定ルート ID を date 単位で localStorage に永続化する hook。
 * リロード時や、ダッシュボード経由の再遷移時に該当日付の選択を自動復元する。
 *
 * 増殖防止のため書き込み時に LRU で {@link MAX_ENTRIES} 件に整形する。
 */
export function useSelectedFixedRouteId(date: string) {
  const [id, setIdState] = useState<number | null>(() => {
    if (!date) return null;
    return readStore()[date]?.fixedRouteId ?? null;
  });

  // 日付変更時に該当 entry を復元 (無ければ null)
  useEffect(() => {
    if (!date) {
      setIdState(null);
      return;
    }
    setIdState(readStore()[date]?.fixedRouteId ?? null);
  }, [date]);

  const setId = useCallback((next: number | null) => {
    setIdState(next);
    if (!date) return;
    const store = readStore();
    if (next == null) {
      delete store[date];
    } else {
      store[date] = { fixedRouteId: next, updatedAt: Date.now() };
    }
    writeStore(store);
  }, [date]);

  return [id, setId] as const;
}
